import { CheckCircle, Lightbulb, Lock, RotateCcw } from "lucide-react";
import { useState } from "react";
import { API_ENDPOINTS, TICKET_TRANSITIONS } from "../../config/constants";
import { useTicketTransition } from "../../hooks/useTicketMutations";
import type { TicketStatus } from "../../types/ticket.types";
import { ProposeSolutionModal } from "../detail/ProposeSolutionModal";

interface TicketTransitionActionsProps {
  ticketId: number | string;
  currentStatus: TicketStatus;
}

const TRANSITION_LABELS: Record<string, { label: string; icon: any; color: string }> = {
  proposition_solution: { label: "Proposer une solution", icon: Lightbulb, color: "btn-info" },
  closed: { label: "Fermer", icon: Lock, color: "btn-success" },
  open: { label: "Réouvrir", icon: RotateCcw, color: "btn-outline" },
  resolved: { label: "Marquer résolu", icon: CheckCircle, color: "btn-success" },
};

// Statut cible → endpoint de transition (les noms de statuts ne matchent pas les clés API)
const TRANSITION_ENDPOINTS: Record<string, (id: number | string) => string> = {
  proposition_solution: API_ENDPOINTS.TICKETS.PROPOSE_SOLUTION,
  closed: API_ENDPOINTS.TICKETS.CLOSE,
  open: API_ENDPOINTS.TICKETS.REOPEN,
  resolved: API_ENDPOINTS.TICKETS.VALIDATE_SOLUTION,
};

export function TicketTransitionActions({ ticketId, currentStatus }: TicketTransitionActionsProps) {
  const transition = useTicketTransition();
  const allowedTransitions = TICKET_TRANSITIONS[currentStatus] ?? [];
  const [showProposeModal, setShowProposeModal] = useState(false);

  const handleTransition = (targetStatus: string) => {
    if (targetStatus === "proposition_solution") {
      setShowProposeModal(true);
      return;
    }
    const endpoint = TRANSITION_ENDPOINTS[targetStatus];
    if (!endpoint) return;
    transition.mutate({
      id: ticketId,
      endpoint: endpoint(ticketId),
    });
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {allowedTransitions.map((targetStatus) => {
          const config = TRANSITION_LABELS[targetStatus];
          if (!config) return null;
          const Icon = config.icon;

          return (
            <button
              key={targetStatus}
              type="button"
              className={`btn btn-sm gap-1.5 ${config.color}`}
              onClick={() => handleTransition(targetStatus)}
              disabled={transition.isPending}
            >
              {transition.isPending ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <Icon className="w-3.5 h-3.5" />
              )}
              {config.label}
            </button>
          );
        })}
      </div>
      <ProposeSolutionModal
        isOpen={showProposeModal}
        onClose={() => setShowProposeModal(false)}
        ticketId={ticketId}
      />
    </>
  );
}
